
import { filter, takeUntil, mergeMap, debounceTime, scan, switchMap, map, shareReplay } from 'rxjs/operators'
import { combineEpics } from 'redux-observable'
import { Epic } from 'interfaces/middleware'
import {
  feedComposeFetchLinkActionCreator,
  FEED_COMPOSE_FETCH_LINK,
  FEED_COMPOSE_IMAGE_REMOVE,
  feedComposeImageRemoveActionCreator,
  FEED_COMPOSE_CLEAN_UP,
  feedComposeCleanUpActionCreator,
  FEED_COMPOSE_ADD_IMAGE_ATTEMPT,
  feedComposeAddImageAttemptActionCreator,
  SUGGEST_USERS,
  suggestUsersActionCreator,
  SuggestUsersRequest,
  SUGGEST_TAGS,
  suggestTagsActionCreator,
  COMPOSE_FOCUS,
  composeExecCommandActionCreator,
  COMPOSE_SUBMIT,
  SuggestTagsRequest,
  SuggestUsersApiPayload,
} from 'actions/feedComposeActions'
import { post, get } from 'helpers/observableAjaxHelpers'
import { Observable, merge, empty, of, from, asyncScheduler } from 'rxjs'
import { Store } from 'interfaces/state'
import { POST_MEDIA_LINK_URL, SUGGEST_USERS_URL, GET_TAGS_URL } from 'interfaces/ajax'
import { isImageType } from 'helpers/DOMHelpers'
import { FEED_POST_MAX_IMAGES_COUNT, FEED_SUGGESTS_LIMIT } from 'constants/feed'
import { Action } from 'actions'
import { userImageUploadActionCreator } from 'actions/userImageActions'
import { composeImagesSelector, composeSelector } from 'selectors/feedComposeSelectors'
import { UserImageId } from 'interfaces/userImage'
import { getFirstUrl } from 'helpers/mediaHelpers'
import { composeAction$ } from 'epics/composeSubmitEpic'
import { viewportSegmentSelector } from 'selectors/viewportSelectors'
import { getScrollAnchor } from 'helpers/feedHelpers'
import { activateAnchorActionCreator } from 'actions/navigationActions'
import { GetFeedTagsRequest } from 'actions/feedActions'
import { byType } from 'rxOperators/byType'
import { filterRequestOfType } from 'rxOperators/filterRequestOfType'
import { ajaxCatch } from 'rxOperators/ajaxCatch'
import { catchAjaxSafe } from 'rxOperators/catchSafe'

const focusEpic: Epic = (action$, store: Store) =>
  action$
    .pipe(byType(COMPOSE_FOCUS)).pipe(
      mergeMap((action) => {
        const state = store.getState()
        const { composeId } = action.payload
        const composeState = composeSelector(state, action.payload)
        if (!composeState) {
          return empty()
        }

        const isDesktop = viewportSegmentSelector(state) === 'desktop'
        const actions: Action[] = []

        const scrollAnchor = getScrollAnchor(composeState, isDesktop)
        if (scrollAnchor) {
          actions.push(activateAnchorActionCreator(scrollAnchor, {
            behavior: 'smooth',
            block: isDesktop ? 'nearest' : 'start',
            preventScrollWhenVisible: isDesktop,
          }))
        }

        actions.push(composeExecCommandActionCreator({
          composeId,
          command: {
            type: 'focus',
          },
        }))

        return of(...actions)
      }))

const removeImageEpic: Epic = (action$, store: Store) =>
  action$
    .pipe(filterRequestOfType(FEED_COMPOSE_IMAGE_REMOVE, 'request')).pipe(
      map((action) => {
        const { params } = action.payload
        const { id } = params
        const userImage = store.getState().app.userImages.byKey[id]
        if (userImage && userImage.localImage) {
          URL.revokeObjectURL(getFirstUrl(userImage.localImage))
        }
        return feedComposeImageRemoveActionCreator.success(action.payload.params, {})
      }))

const cleanUpEpic: Epic = (action$, store: Store) =>
  action$
    .pipe(filterRequestOfType(FEED_COMPOSE_CLEAN_UP, 'request')).pipe(
      map((action) => {
        const { params } = action.payload
        const state = store.getState()
        const images = composeImagesSelector(state, params)
        images.forEach((userImage) => {
          if (userImage && userImage.localImage) {
            URL.revokeObjectURL(getFirstUrl(userImage.localImage))
          }
        })

        return feedComposeCleanUpActionCreator.success(params, {})
      }))


const linksFetchEpic: Epic = (action$, store: Store) =>
  action$
    .pipe(filterRequestOfType(FEED_COMPOSE_FETCH_LINK, 'request')).pipe(
      mergeMap((action) => {
        const { params } = action.payload
        const { url, composeId } = params
        const composeState = store.getState().app.feed.composes.byKey[composeId]

        if (!composeState) {
          throw new Error('unknown app state')
        }

        const isLinkToBeFetched = !composeState.link || (composeState.isLinkRemoved && composeState.link.url !== url)
        if (!isLinkToBeFetched) {
          return of(feedComposeFetchLinkActionCreator.fail(params, 0))
        }

        return post(POST_MEDIA_LINK_URL, { body: { url }}).pipe(
          takeUntil(
            composeAction$(action$, store).pipe(
              filter((event) => event.composeState.id === composeId))
          ),
          mergeMap(({ response }) => {
            if (response.type === 'other') {
              return of<Action>(feedComposeFetchLinkActionCreator.fail(params, 0))
            }

            const successAction = of(feedComposeFetchLinkActionCreator.success(params, response))
            if (response.type === 'image' && !!response.image) {
              return merge(
                successAction,
                of(feedComposeAddImageAttemptActionCreator.request({
                  imagesIds: [response.image.id],
                  composeId,
                }))
              )
            }
            return successAction
          }))
          .pipe(catchAjaxSafe((error) =>
            of(feedComposeFetchLinkActionCreator.fail(params, error.status))
          ))
      }))

type ImageInput = {
  file?: File,
  imageId?: UserImageId
}
const composeAddImagesAttemptEpic: Epic = (action$, store: Store) =>
  action$
    .pipe(filterRequestOfType(FEED_COMPOSE_ADD_IMAGE_ATTEMPT, 'request')).pipe(
      mergeMap((action) => {
        const { composeId, imagesIds, files } = action.payload.params
        const state = store.getState()
        const composeState = state.app.feed.composes.byKey[composeId]

        if (!composeState) {
          throw new Error('unknown app state')
        }

        let imagesLeftCount = FEED_POST_MAX_IMAGES_COUNT - composeState.imagesIds.length
        if (imagesLeftCount <= 0 || composeState.isOpportunityReview) {
          return of(null)
        }

        const imagesToAdd: ImageInput[] = []
        const imagesInput: ImageInput[] = [
          ...files ? files.map((file) => ({ file })) : [],
          ...imagesIds ? imagesIds.map((id) => ({ imageId: id })) : [],
        ]
        imagesInput.find((imageInput) => {
          const isImage = !!imageInput.imageId || (imageInput.file && isImageType(imageInput.file.type))
          if (isImage) {
            imagesToAdd.push(imageInput)
            imagesLeftCount--
          }
          return imagesLeftCount <= 0
        })

        if (imagesToAdd.length === 0) {
          return of(null)
        }

        return from(imagesToAdd).pipe(map((imageInput) => ({
          imageInput,
          action,
        })))
      }),
      mergeMap((event) => {
        if (!event) {
          return empty()
        }

        const { imageInput, action } = event
        const { imageId, file } = imageInput

        const actions: Action[] = []
        const localId = -1 * Math.random()
        if (imageId || file) {
          actions.push(feedComposeAddImageAttemptActionCreator.success(action.payload.params, {
            imageId: imageId || localId,
          }))
        }

        if (file) {
          actions.unshift(
            userImageUploadActionCreator.request({
              id: localId,
              file,
              type: 'feed',
            }))
        }

        return from(actions)
      }))

type SuggestsAccType = {
  cache: Record<string, Observable<Action>>
  request: Observable<Action>
}
const suggestUsersEpic: Epic = (action$, _store, deps) => merge(
  of(null),
  action$
    .pipe(filterRequestOfType(COMPOSE_SUBMIT, 'request'))
).pipe(
  switchMap(() =>
    action$
      .pipe(filterRequestOfType(SUGGEST_USERS, 'request')).pipe(
        debounceTime(300, deps.scheduler ?? asyncScheduler),
        map(({ payload }) => payload.params),
        scan<SuggestUsersRequest, SuggestsAccType>((acc, params) => {
          const { postUid, search } = params
          const key = [search, postUid || null].join('_')
          let request = acc.cache[key]
          if (!request) {
            const apiPayload: SuggestUsersApiPayload = {
              post_uid: postUid,
              search,
            }
            request = acc.cache[key] = get(SUGGEST_USERS_URL, apiPayload).pipe(
              map(({ response }) => suggestUsersActionCreator.success(params, response.items)))
              .pipe(ajaxCatch((error) => of(suggestUsersActionCreator.fail(params, error.status))))
              .pipe(shareReplay(1))
          }

          return {
            ...acc,
            request,
          }
        }, {
          cache: {},
          request: empty(),
        }),
        mergeMap(({ request }) => request))
  ))

const suggestTagsEpic: Epic = (action$, _store, deps) => merge(
  of(null),
  action$
    .pipe(filterRequestOfType(COMPOSE_SUBMIT, 'request'))
).pipe(
  switchMap(() =>
    action$
      .pipe(filterRequestOfType(SUGGEST_TAGS, 'request')).pipe(
        debounceTime(300, deps.scheduler ?? asyncScheduler),
        map(({ payload }) => payload.params),
        scan<SuggestTagsRequest, SuggestsAccType>((acc, params) => {
          const { search } = params
          const key = search
          let request = acc.cache[key]
          if (!request) {
            const apiPayload: GetFeedTagsRequest = {
              search,
              limit: FEED_SUGGESTS_LIMIT,
            }
            request = acc.cache[key] = get(GET_TAGS_URL, apiPayload).pipe(
              map(({ response }) => suggestTagsActionCreator.success(params, response.items)))
              .pipe(ajaxCatch((error) => of(suggestTagsActionCreator.fail(params, error.status))))
              .pipe(shareReplay(1))
          }

          return {
            ...acc,
            request,
          }
        }, {
          cache: {},
          request: empty(),
        }),
        mergeMap(({ request }) => request))
  ))

export const composeEpic = combineEpics(
  focusEpic,
  removeImageEpic,
  cleanUpEpic,
  linksFetchEpic,
  composeAddImagesAttemptEpic,
  suggestUsersEpic,
  suggestTagsEpic,
)

